import { useCallback, useEffect, useRef, useState } from 'react';
import type { DailyRoutine } from '../application/daily-routine.ts';
import type { HomeDashboard } from '../application/home-dashboard.ts';
import { Home } from './home.tsx';
import { Training } from './training.tsx';
import type { TrainingApps } from './training.tsx';
import { Nutrition } from './nutrition.tsx';
import type { NutritionApps } from './nutrition.tsx';
import { Progress } from './progress.tsx';
import type { ProgressApps } from './progress.tsx';
import type { BackupExport } from '../application/backup-export.ts';
import type { RestoreManager } from '../application/restore.ts';
import type { RestoreRecoveryOutcome, RestoreResult } from '../domain/restore.ts';
import { Settings } from './settings.tsx';
import { Confirmation } from './primitives.tsx';
import './tokens.css';
import './system.css';

const destinations = ['Home', 'Training', 'Nutrition', 'Progress'] as const;
function localToday() {
  const now = new Date();
  return [now.getFullYear(), String(now.getMonth() + 1).padStart(2, '0'), String(now.getDate()).padStart(2, '0')].join('-');
}
function route() { const name = location.hash.slice(1); return name.startsWith('Training/') || name.startsWith('Nutrition/') || name.startsWith('Progress/') || [...destinations, 'Settings'].includes(name) ? name : 'Home'; }
export function App({
  app,
  home,
  homeLocalDate,
  checkId,
  training,
  nutrition,
  progress,
  backup,
  restore,
  restoreRecovery,
}: {
  app: DailyRoutine;
  home: HomeDashboard | null;
  homeLocalDate?: string;
  checkId: string;
  training: TrainingApps | null;
  nutrition: NutritionApps | null;
  progress: ProgressApps | null;
  backup: BackupExport | null;
  restore: RestoreManager | null;
  restoreRecovery: RestoreResult<RestoreRecoveryOutcome>;
}) {
  const [today, setToday] = useState(localToday);
  useEffect(() => {
    if (homeLocalDate !== undefined) return;
    const refreshDate = () => setToday(localToday());
    const timer = setInterval(refreshDate, 30_000);
    addEventListener('focus', refreshDate);
    document.addEventListener('visibilitychange', refreshDate);
    return () => {
      clearInterval(timer);
      removeEventListener('focus', refreshDate);
      document.removeEventListener('visibilitychange', refreshDate);
    };
  }, [homeLocalDate]);
  const [current, setCurrent] = useState(route);
  const currentRef = useRef(current);
  const dirtyRef = useRef(false);
  const pendingRef = useRef(false);
  const [waiting, setWaiting] = useState(false);
  const [session, setSession] = useState(false);
  const sessionMode = useCallback((value: boolean) => setSession(value), []);
  const [leaving, setLeaving] = useState<string | null>(null);
  const dirty = useCallback((value: boolean) => { dirtyRef.current = value; }, []);
  const pending = useCallback((value: boolean) => { pendingRef.current = value; if (!value) setWaiting(false); }, []);
  const navigate = useCallback((path: string) => {
    if (path === currentRef.current) return;
    if (pendingRef.current) { setWaiting(true); return; }
    if (dirtyRef.current) setLeaving(path);
    else location.hash = path;
  }, []);
  const bottom = useRef<HTMLElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const update = () => {
      const next = route();
      if (next === currentRef.current) return;
      if (pendingRef.current) { history.replaceState(null, '', `#${currentRef.current}`); setWaiting(true); return; }
      if (dirtyRef.current) { history.replaceState(null, '', `#${currentRef.current}`); setLeaving(next); return; }
      currentRef.current = next; setCurrent(next);
    };
    const unload = (event: BeforeUnloadEvent) => { if (dirtyRef.current || pendingRef.current) { event.preventDefault(); event.returnValue = ''; } };
    addEventListener('hashchange', update);
    addEventListener('beforeunload', unload);
    return () => { removeEventListener('hashchange', update); removeEventListener('beforeunload', unload); };
  }, []);
  useEffect(() => {
    const measure = () => shell.current?.style.setProperty('--bottom-rendered', `${bottom.current?.getBoundingClientRect().height ?? 0}px`);
    const observer = new ResizeObserver(measure);
    if (bottom.current) observer.observe(bottom.current);
    measure(); return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (current.startsWith('Training') || current.startsWith('Progress')) {
      const title = [...document.querySelectorAll<HTMLElement>('main h1')].find(el => el.getClientRects().length);
      if (title) { title.tabIndex = -1; title.focus(); }
    }
  }, [current]);
  return <div className={`app-shell${session ? ' session-shell' : ''}`} ref={shell} onClickCapture={event => {
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    const href = anchor?.getAttribute('href');
    if (href?.startsWith('#')) { event.preventDefault(); navigate(href.slice(1)); }
  }}>
    {session ? <header className="shell-header"><div className="header-content"><a className="settings-link" href="#Training/Hoy">Salir y continuar después</a><span>Training · sesión activa</span></div></header> : <header className="shell-header"><div className="header-content">
      <span className="brand-name">FÉNIX</span>
      <a className="settings-link" href="#Settings">Ajustes</a>
      <img
  className="brand-logo"
  src="/assets/fenix-brand.png"
  alt=""
  aria-hidden="true"
  draggable={false}
  data-testid="brand-decorative"
/>
    </div></header>}
    <main className="shell-main">
      <p className="lab-notice">Laboratorio G5 · Solo datos sintéticos</p>
      {waiting ? <p role="status">Espera a que termine el guardado antes de salir.</p> : null}
      <div hidden={current !== 'Home'}><Home app={app} home={home} localDate={homeLocalDate ?? today} checkId={checkId} active={current === 'Home'} /></div>
      {current.startsWith('Training') ? <Training key={current} apps={training} path={current} navigate={navigate} dirty={dirty} pending={pending} sessionMode={sessionMode} />
        : current.startsWith('Nutrition') ? <Nutrition key={current} apps={nutrition} path={current} navigate={navigate} dirty={dirty} pending={pending} />
        : current.startsWith('Progress') ? <Progress key={current} apps={progress} path={current} navigate={navigate} dirty={dirty} pending={pending} /> : current === 'Settings' ? <Settings backup={backup} restore={restore} restoreRecovery={restoreRecovery} pending={pending} /> : current !== 'Home' ? <div className="root-page"><h1>{current}</h1>
        <p>Destino de navegación de prueba. Su funcionalidad está fuera de este corte.</p>
        <a className="settings-link" href="#Home">Volver a Home</a>
      </div> : null}
    </main>
    <nav hidden={session} className="bottom-nav" aria-label="Navegación principal" ref={bottom}>
      <div className="nav-content">{destinations.map(destination => <a key={destination} href={`#${destination}`}
        aria-current={current.split('/')[0] === destination ? 'page' : undefined}>
        <span className="nav-icon-slot" aria-hidden="true" /><span>{destination}</span>
      </a>)}</div>
    </nav>
    {leaving ? <Confirmation title="Cambios sin guardar" message="Puedes seguir editando o descartar los cambios del formulario." confirmLabel="Descartar y salir"
      onAnswer={answer => { const target = leaving; setLeaving(null); if (answer) { dirty(false); navigate(target); } }} /> : null}
  </div>;
}
